import React, { useState, useEffect } from 'react';
import SideBar from '../components/SideBar';
import '../styles/Dashboard.css';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Label,
  ResponsiveContainer,
} from 'recharts';
import ApiService from '../services/api';

function Dashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [topcoinData, setTopcoinData] = useState([]);
  const [bonusData, setBonusData] = useState([]);
  const [mergedData, setMergedData] = useState([]);
  const [usersData, setUsersData] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [overallStats, setOverallStats] = useState(null);
  const [userStats, setUserStats] = useState(null);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Format number with commas
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return Number(num).toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };
// Process user data from API response
const processUserData = (apiResponse, selectedYear) => {
  try {
    console.log('Raw API response:', apiResponse);
    
    if (apiResponse.success && apiResponse.data) {
      const monthlyData = apiResponse.data.monthly_counts || apiResponse.data;
      console.log('Monthly data from API:', monthlyData);
      
      // Filter data for the selected year only
      const yearData = monthlyData.filter(item => item.year === selectedYear);
      console.log(`Data for year ${selectedYear}:`, yearData);
      
      // If we don't have data for selected year, use all data
      const dataToProcess = yearData.length > 0 ? yearData : monthlyData;
      
      // Create array for all months with zeros
      const allMonthsData = months.map(month => ({
        month,
        Member: 0,
        RetailerShop: 0,
        Agent: 0,
        Customer: 0,
        Total: 0
      }));
      
      // Fill data from API response
      dataToProcess.forEach(item => {
        const monthIndex = months.findIndex(m => 
          m.toLowerCase() === item.month.toLowerCase()
        );
        
        if (monthIndex !== -1) {
          allMonthsData[monthIndex] = {
            month: months[monthIndex],
            Member: item.Member || 0,
            RetailerShop: item.RetailerShop || 0,
            Agent: item.Agent || 0,
            Customer: item.Customer || 0,
            Total: item.total || 0
          };
        }
      });
      
      console.log('Processed chart data:', allMonthsData);
      return allMonthsData;
    }
    
    return [];
  } catch (err) {
    console.error('Error processing user data:', err);
    return [];
  }
};

  // Merge points and bonus data into single dataset
  const mergePointsAndBonusData = (pointsData, bonusData) => {
    return months.map((month, index) => ({
      month,
      Points: pointsData[index]?.amount || 0,
      Bonus: bonusData[index]?.amount || 0,
      formattedPoints: formatNumber(pointsData[index]?.amount || 0),
      formattedBonus: formatNumber(bonusData[index]?.amount || 0),
    }));
  };

  // Fetch user chart data
  const fetchUserData = async (selectedYear = null) => {
    try {
      let response;
      if (selectedYear && selectedYear !== year) {
        response = await ApiService.getUserCountsByYear(selectedYear);
      } else {
        response = await ApiService.getUserCountsByMonth();
      }

      if (response.success) {
        const processedData = processUserData(response, selectedYear || year);
        setUsersData(processedData);
        
        // Store additional user stats if available
        if (response.data.summary) {
          setUserStats(response.data.summary);
        }
      } else {
        throw new Error(response.message || 'Failed to fetch user data');
      }
    } catch (err) {
      console.error('Failed to fetch user data:', err);
      // Fallback to dummy data if API fails
      setUsersData(getFallbackUserData());
    }
  };

  // Fallback user data (similar to your dummy data)
  const getFallbackUserData = () => {
    return months.map((month) => ({
      month,
      Member: Math.floor(Math.random() * 200),
      RetailerShop: Math.floor(Math.random() * 150),
      Agent: Math.floor(Math.random() * 100),
      Customer: Math.floor(Math.random() * 500),
      Total: 0
    }));
  };

  // Fetch wallet chart data
  const fetchWalletData = async (selectedYear = null) => {
    try {
      setLoading(true);
      setError(null);

      let response;
      if (selectedYear && selectedYear !== year) {
        response = await ApiService.getPublicWalletChartDataByYear(selectedYear);
        setYear(selectedYear);
      } else {
        response = await ApiService.getPublicWalletChartData();
      }

      const { monthly_points, monthly_users, overall_stats } = response;

      // Prepare Topcoins data (monthly points)
      const topcoinDataArray = monthly_points.map((points, index) => ({
        month: months[index],
        amount: points || 0,
        formattedAmount: formatNumber(points || 0),
      }));

      // Prepare Bonus data (points * 0.005)
      const bonusDataArray = monthly_points.map((points, index) => {
        const bonusAmount = (points || 0) * 0.005;
        return {
          month: months[index],
          amount: bonusAmount,
          formattedAmount: formatNumber(bonusAmount),
        };
      });

      // Merge points and bonus data
      const mergedChartData = mergePointsAndBonusData(topcoinDataArray, bonusDataArray);

      setTopcoinData(topcoinDataArray);
      setBonusData(bonusDataArray);
      setMergedData(mergedChartData);
      setOverallStats(overall_stats);
    } catch (err) {
      console.error('Failed to fetch wallet data:', err);
      setError(err.message || 'Failed to load dashboard data');
      // Fallback to dummy data if API fails
      const dummyTopcoinData = getDummyTopcoinData();
      const dummyBonusData = getDummyBonusData();
      const mergedDummyData = mergePointsAndBonusData(dummyTopcoinData, dummyBonusData);
      
      setTopcoinData(dummyTopcoinData);
      setBonusData(dummyBonusData);
      setMergedData(mergedDummyData);
    } finally {
      setLoading(false);
    }
  };

  // Dummy data fallback functions for other charts
  const getDummyTopcoinData = () => {
    return months.map((month) => ({
      month,
      amount: Math.floor(Math.random() * 10000),
      formattedAmount: formatNumber(Math.floor(Math.random() * 10000)),
    }));
  };

  const getDummyBonusData = () => {
    return months.map((month) => {
      const points = Math.floor(Math.random() * 10000);
      const bonusAmount = points * 0.005;
      return {
        month,
        amount: bonusAmount,
        formattedAmount: formatNumber(bonusAmount),
      };
    });
  };

  // Fetch all data
  const fetchAllData = async (selectedYear = null) => {
    setLoading(true);
    try {
      await Promise.all([
        fetchWalletData(selectedYear),
        fetchUserData(selectedYear)
      ]);
    } catch (err) {
      console.error('Error fetching all data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  // Custom tooltip component for merged chart
  const MergedChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: 'white',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            minWidth: '180px',
          }}
        >
          <p className="label" style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '12px' }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '4px 0',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    backgroundColor: entry.color,
                    marginRight: '8px',
                    borderRadius: '2px',
                  }}
                />
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {entry.dataKey}:
                </span>
              </div>
              <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
                {entry.dataKey === 'Points' 
                  ? formatNumber(entry.value)
                  : `${formatNumber(entry.value)}`
                }
              </span>
            </div>
          ))}
        </div>
      );
    }
    return null;
  };

  // Custom tooltip component for users chart
  const UsersChartTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          className="custom-tooltip"
          style={{
            backgroundColor: 'white',
            padding: '10px',
            border: '1px solid #ccc',
            borderRadius: '5px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            minWidth: '200px',
          }}
        >
          <p className="label" style={{ margin: '0 0 8px 0', fontWeight: 'bold', fontSize: '12px' }}>
            {label}
          </p>
          {payload.map((entry, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                margin: '4px 0',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <div
                  style={{
                    width: '10px',
                    height: '10px',
                    backgroundColor: entry.color,
                    marginRight: '8px',
                    borderRadius: '2px',
                  }}
                />
                <span style={{ fontSize: '12px', color: '#666' }}>
                  {entry.dataKey}:
                </span>
              </div>
              <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
                {formatNumber(entry.value)}
              </span>
            </div>
          ))}
          <hr style={{ margin: '8px 0' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>Total:</span>
            <span style={{ fontSize: '12px', fontWeight: 'bold' }}>
              {formatNumber(payload.reduce((sum, entry) => sum + entry.value, 0))}
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  // Year selection handler
  const handleYearChange = (e) => {
    const selectedYear = parseInt(e.target.value);
    fetchAllData(selectedYear);
  };

  // Generate year options (last 5 years)
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);

  if (loading) {
    return (
      <div className="manage-member-layout">
        <SideBar />
        <div className="main-content">
          <div className="header-bar" style={{border:0, boxShadow:"none"}}>
            <div className="header-top">
              <h2 className="header-title">Dashboard</h2>
            </div>
          </div>
          <div className="dashboard-loading">
            <div className="loading-spinner"></div>
            <p>Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Calculate user statistics
  const userStatistics = userStats || {
    total_members: usersData.reduce((sum, item) => sum + item.Member, 0),
    total_retailers: usersData.reduce((sum, item) => sum + item.RetailerShop, 0),
    total_agents: usersData.reduce((sum, item) => sum + item.Agent, 0),
    total_customers: usersData.reduce((sum, item) => sum + item.Customer, 0),
    grand_total: usersData.reduce((sum, item) => sum + item.Total, 0)
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{border:0, boxShadow:"none"}}>
          <div className="header-top">
            <h2 className="header-title">Dashboard</h2>
            {/* <div className="year-selector">
              <label htmlFor="year-select">Year: </label>
              <select 
                id="year-select" 
                value={year}
                onChange={handleYearChange}
                style={{
                  padding: '5px 10px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                  backgroundColor: 'white'
                }}
              >
                {yearOptions.map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div> */}
          </div>
        </div>

        {error && (
          <div
            className="error-message"
            style={{
              backgroundColor: '#FEE2E2',
              border: '1px solid #FCA5A5',
              color: '#DC2626',
              padding: '10px',
              borderRadius: '4px',
              margin: '20px',
              textAlign: 'center',
            }}
          >
            {error} (Showing sample data)
          </div>
        )}

        {/* User Statistics Cards */}
        {/* <div className="stats-cards" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          margin: '20px 0',
        }}>
          <div className="stat-card" style={{
            backgroundColor: '#EAB308',
            padding: '20px',
            borderRadius: '8px',
            color: 'white',
          }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Total Members</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
              {formatNumber(userStatistics.total_members)}
            </p>
          </div>
          <div className="stat-card" style={{
            backgroundColor: '#F97316',
            padding: '20px',
            borderRadius: '8px',
            color: 'white',
          }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Total Retailers</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
              {formatNumber(userStatistics.total_retailers)}
            </p>
          </div>
          <div className="stat-card" style={{
            backgroundColor: '#22C55E',
            padding: '20px',
            borderRadius: '8px',
            color: 'white',
          }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Total Agents</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
              {formatNumber(userStatistics.total_agents)}
            </p>
          </div>
          <div className="stat-card" style={{
            backgroundColor: '#3B82F6',
            padding: '20px',
            borderRadius: '8px',
            color: 'white',
          }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Total Customers</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
              {formatNumber(userStatistics.total_customers)}
            </p>
          </div>
          <div className="stat-card" style={{
            backgroundColor: '#8B5CF6',
            padding: '20px',
            borderRadius: '8px',
            color: 'white',
          }}>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Grand Total</h3>
            <p style={{ margin: 0, fontSize: '24px', fontWeight: 'bold' }}>
              {formatNumber(userStatistics.grand_total)}
            </p>
          </div>
        </div> */}

        <div className="dashboard-container">
          {/* Merged Points and Bonus Chart */}
          <div className="chart-card full-width">
            <h3 className="chart-title">TopCoins & Bonus by Month ({year})</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={mergedData}>
                <defs>
                  <linearGradient id="colorPoints" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="50%" stopColor="#EF4444" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorBonus" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="50%" stopColor="#3B82F6" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ className: 'axis-tick' }}>
                  <Label
                    value={`Month (Jan - Dec ${year})`}
                    offset={-5}
                    position="insideBottom"
                    style={{ fill: '#555', fontSize: 10 }}
                  />
                </XAxis>

                <YAxis yAxisId="left" tick={{ className: 'axis-tick' }}>
                  <Label
                    value="TopCoins"
                    angle={-90}
                    position="insideLeft"
                    offset={0}
                    style={{ textAnchor: 'middle', fill: '#555', fontSize: 10 }}
                  />
                </YAxis>

                <YAxis yAxisId="right" orientation="right" tick={{ className: 'axis-tick' }}>
                  <Label
                    value="Bonus (Rs)"
                    angle={-90}
                    position="insideRight"
                    offset={0}
                    style={{ textAnchor: 'middle', fill: '#555', fontSize: 10 }}
                  />
                </YAxis>

                <Tooltip content={<MergedChartTooltip />} />
                <Legend
                  layout="vertical"
                  verticalAlign="top"
                  align="right"
                  wrapperStyle={{
                    border: '1px solid #ccc',
                    backgroundColor: '#fff',
                    padding: '0px 5px',
                    borderRadius: '5px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    marginTop: '-65px',
                    fontSize: '10px',
                  }}
                />
                
                <Area
                  yAxisId="left"
                  type="monotone"
                  dataKey="Points"
                  stroke="#EF4444"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorPoints)"
                  dot={{ stroke: '#EF4444', fill: '#EF4444', r: 3 }}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                  name="TopCoins"
                />
                
                <Area
                  yAxisId="right"
                  type="monotone"
                  dataKey="Bonus"
                  stroke="#3B82F6"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorBonus)"
                  dot={{ stroke: '#3B82F6', fill: '#3B82F6', r: 3 }}
                  activeDot={{ r: 5, strokeWidth: 2 }}
                  name="Bonus"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Users Count Chart */}
          <div className="chart-card full-width">
            <h3 className="chart-title">Users Count by Month ({year})</h3>
            <ResponsiveContainer width="100%" height={250}>
              <AreaChart data={usersData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ className: 'axis-tick' }}>
                  <Label
                    value={`Month (Jan - Dec ${year})`}
                    offset={-5}
                    position="insideBottom"
                    style={{ fill: '#555', fontSize: 10 }}
                  />
                </XAxis>

                <YAxis tick={{ className: 'axis-tick' }}>
                  <Label
                    value="Users Count"
                    angle={-90}
                    position="insideLeft"
                    offset={0}
                    style={{ textAnchor: 'middle', fill: '#555', fontSize: 10 }}
                  />
                </YAxis>
                <Tooltip content={<UsersChartTooltip />} />
                <Legend
                  layout="vertical"
                  verticalAlign="top"
                  align="right"
                  wrapperStyle={{
                    border: '1px solid #ccc',
                    backgroundColor: '#fff',
                    padding: '0px 5px',
                    borderRadius: '5px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    marginTop: '-65px',
                    fontSize: '10px',
                  }}
                />

                <defs>
                  <linearGradient id="colorMember" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="100%" stopColor="#EAB308" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#EAB308" stopOpacity={0} />
                  </linearGradient>

                  <linearGradient id="colorRetailerShop" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="100%" stopColor="#F97316" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#F97316" stopOpacity={0} />
                  </linearGradient>

                  <linearGradient id="colorAgent" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="100%" stopColor="#22C55E" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#22C55E" stopOpacity={0} />
                  </linearGradient>

                  <linearGradient id="colorCustomer" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="100%" stopColor="#3B82F6" stopOpacity={0.6} />
                    <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <Area
                  type="monotone"
                  dataKey="Member"
                  stroke="#EAB308"
                  strokeWidth={1}
                  fill="url(#colorMember)"
                  dot={{ stroke: '#EAB308', fill: '#EAB308', r: 3 }}
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="RetailerShop"
                  stroke="#F97316"
                  strokeWidth={1}
                  fill="url(#colorRetailerShop)"
                  dot={{ stroke: '#F97316', fill: '#F97316', r: 3 }}
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="Agent"
                  stroke="#22C55E"
                  strokeWidth={1}
                  fill="url(#colorAgent)"
                  dot={{ stroke: '#22C55E', fill: '#22C55E', r: 3 }}
                  activeDot={{ r: 4 }}
                />
                <Area
                  type="monotone"
                  dataKey="Customer"
                  stroke="#3B82F6"
                  strokeWidth={1}
                  fill="url(#colorCustomer)"
                  dot={{ stroke: '#3B82F6', fill: '#3B82F6', r: 3 }}
                  activeDot={{ r: 4 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
